import React, { useState, useRef, useEffect } from 'react';

const LazyImage = ({ 
  src, 
  alt, 
  className = '', 
  fallbackSrc = '/default-anime.jpg',
  threshold = 0.1  // When image is 10% visible, start loading it
}) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);
  
  // Set up the intersection observer to detect when image is near viewport
  useEffect(() => {
    let observer;
    let isActive = true; // Flag to prevent state updates if component unmounts
    
    const loadImage = () => {
      // Only load image if we haven't already tried to load it
      if (!imageSrc && !hasError && isActive) {
        // Set the image source to trigger loading
        setImageSrc(src);
      }
    };
    
    // Create observer to watch when element comes into view
    observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && isActive) {
          loadImage();
          // Once we've started loading, disconnect the observer
          observer.disconnect();
        }
      },
      {
        rootMargin: '200px', // Start loading when within 200px of viewport
        threshold
      }
    );
    
    // Start observing the image element
    if (imgRef.current) {
      observer.observe(imgRef.current);
    }
    
    return () => {
      isActive = false;
      if (observer) {
        observer.disconnect();
      }
    };
  }, [src, imageSrc, hasError, threshold]);
  
  // Handle image load success
  const handleLoad = () => {
    setIsLoaded(true);
  };
  
  // Handle image load error
  const handleError = () => {
    setHasError(true);
    setImageSrc(fallbackSrc);
  };
  
  return (
    <div 
      ref={imgRef}
      className={`lazy-image-container ${!isLoaded ? 'lazy-image-loading' : ''} ${className}`}
    >
      {/* Show placeholder before image is loaded */}
      {!isLoaded && (
        <div className="lazy-image-placeholder">
          {/* You can customize your placeholder here */}
        </div>
      )}
      
      {/* Only render the actual img tag once we've decided to load it */}
      {imageSrc && (
        <img
          src={imageSrc}
          alt={alt}
          className={`lazy-image ${isLoaded ? 'lazy-image-loaded' : ''}`}
          onLoad={handleLoad}
          onError={handleError}
        />
      )}
    </div>
  );
};

export default LazyImage;